import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ArticleService from "../services/ArticleService";
import ImageService from '../services/ImageService';
import BrandService from "../services/BrandService";
import { useParams, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import log from "../services/logger";
import { Button, Input } from './common';

// Form Section Component
const FormSection = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {children}
        </div>
    </div>
);

const EditArticle = () => {
    const [article, setArticle] = useState(null);
    const [brands, setBrands] = useState([]);
    const [existingImage, setExistingImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const { id: articleID } = useParams();
    const navigate = useNavigate();

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            code: "",
            barcode: "",
            name: "",
            unitOfMeasurement: "",
            quantityPerTransportPackage: "",
            minimumQuantityDemand: "",
            brutoMass: "",
            wholesalePrice: "",
            retailPrice: "",
            imageSource: "",
            pdv: "20",
            isActive: 'true',
            isVisible: 'true',
            brandName: "",
            imageFile: null,
            startingQuantityDemand: "",
            maximumQuantityDemand: "",
            minimumTemperature: "",
            maximumTemperature: "",
            expiryDateInMinutes: ""
        },
        validationSchema: Yup.object().shape({
            code: Yup.string().required('Šifra Artikla je obavezna.'),
            barcode: Yup.string().required('Barkod Artikla je obavezan.'),
            name: Yup.string().required('Naziv Artikla je obavezno.'),
            unitOfMeasurement: Yup.string(),
            quantityPerTransportPackage: Yup.number().integer(),
            minimumQuantityDemand: Yup.number().integer(),
            brutoMass: Yup.number(),
            wholesalePrice: Yup.number().required('Veleprodajna cena je obavezna.'),
            retailPrice: Yup.number(),
            imageSource: Yup.string(),
            pdv: Yup.number().required('PDV je obavezan'),
            isActive: Yup.string().required('Obavezno biranje statusa artikla.'),
            isVisible: Yup.string().required('Obavezno biranje vidljivosti artikla.'),
            brandName: Yup.string().required('Naziv brenda je obavezan.'),
            startingQuantityDemand: Yup.number(),
            maximumQuantityDemand: Yup.number(),
            minimumTemperature: Yup.number(),
            maximumTemperature: Yup.number(),
            expiryDateInMinutes: Yup.number()
        }),
        onSubmit: async values => {
            try {
                let isUpdated = false;

                if (values.imageFile) {
                    await ArticleService.updateImage(articleID, values.code, values.imageFile);

                    const imageName = `${values.code}.png`;
                    const fileReader = new FileReader();
                    fileReader.onload = (event) => {
                        const imageData = event.target.result;
                        localStorage.setItem(imageName, imageData);
                        setExistingImage(imageData);  // Update existing image state
                    };
                    fileReader.readAsDataURL(values.imageFile);
                    isUpdated = true;
                }

                if (!isUpdated || !isEqual(article, values)) {
                    await ArticleService.update(articleID, values);
                    toast.success('Uspešno ažuriran artikal!');
                }

                navigate(`/articles`);
            } catch (error) {
                log.info(error);
                toast.error('Neuspešno ažuriranje!');
            }
        }
    });

    useEffect(() => {
        retrieveBrands();
        ArticleService.get(articleID)
            .then(response => {
                setArticle(response.data);
                const cachedUpload = localStorage.getItem(`${response.data.code}.png`);
                setExistingImage(cachedUpload || ImageService.findArticleImage(response.data));
                formik.setValues({
                    code: response.data.code || "",
                    barcode: response.data.barcode || "",
                    name: response.data.name || "",
                    unitOfMeasurement: response.data.unitOfMeasurement || "",
                    quantityPerTransportPackage: response.data.quantityPerTransportPackage || "",
                    minimumQuantityDemand: response.data.minimumQuantityDemand || "",
                    brutoMass: response.data.brutoMass || "",
                    wholesalePrice: response.data.wholesalePrice || "",
                    retailPrice: response.data.retailPrice || "",
                    imageSource: response.data.imageSource || "",
                    pdv: response.data.pdv || "20",
                    isActive: response.data.isActive ? 'true' : 'false',
                    isVisible: response.data.isVisible ? 'true' : 'false',
                    brandName: response.data.brand ? response.data.brand.brandName : "",
                    imageFile: null,
                    startingQuantityDemand: response.data.startingQuantityDemand || "",
                    maximumQuantityDemand: response.data.maximumQuantityDemand || "",
                    minimumTemperature: response.data.minimumTemperature || "",
                    maximumTemperature: response.data.maximumTemperature || "",
                    expiryDateInMinutes: response.data.expiryDateInMinutes || "",
                });
            }).catch(e => {
                toast.error('Neuspešno preuzimanje artikala!', e);
            });
    }, []);

    const retrieveBrands = () => {
        BrandService.getAll()
            .then(response => {
                setBrands(response.data);
            })
            .catch(e => {
                toast.error('Neuspešno preuzimanje brendova!', e);
            });
    };

    const handleBrandChange = (event) => {
        formik.setFieldValue('brandName', event.target.value);
    };

    const handleImageChange = (event) => {
        formik.setFieldValue('imageFile', event.currentTarget.files[0]);
    };

    const isEqual = (obj1, obj2) => {
        for (let key in obj1) {
            if (obj1[key] !== obj2[key]) {
                return false;
            }
        }
        return true;
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Izmena Artikla</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Popunite potrebne informacije za izmenu artikla
                    </p>
                </div>

                <form onSubmit={formik.handleSubmit} className="space-y-6">
                    <FormSection title="Osnovne Informacije">
                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Šifra Artikla
                            </label>
                            <input
                                type="text"
                                name="code"
                                value={formik.values.code}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            {formik.touched.code && formik.errors.code && (
                                <div className="text-red-500 text-xs mt-1">{formik.errors.code}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Barkod Artikla
                            </label>
                            <input
                                type="text"
                                name="barcode"
                                value={formik.values.barcode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            {formik.touched.barcode && formik.errors.barcode && (
                                <div className="text-red-500 text-xs mt-1">{formik.errors.barcode}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Naziv Artikla
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            {formik.touched.name && formik.errors.name && (
                                <div className="text-red-500 text-xs mt-1">{formik.errors.name}</div>
                            )}
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Brend
                            </label>
                            <select
                                name="brandName"
                                value={formik.values.brandName}
                                onChange={handleBrandChange}
                                onBlur={formik.handleBlur}
                                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            >
                                <option value="">Izaberite brend</option>
                                {brands.map((brand) => (
                                    <option key={brand.brand_id} value={brand.brandName}>
                                        {brand.brandName}
                                    </option>
                                ))}
                            </select>
                            {formik.touched.brandName && formik.errors.brandName && (
                                <p className="mt-2 text-sm text-red-600">{formik.errors.brandName}</p>
                            )}
                        </div>
                    </FormSection>

                    <FormSection title="Detalji Proizvoda">
                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Jedinica Mere
                            </label>
                            <input
                                type="text"
                                name="unitOfMeasurement"
                                value={formik.values.unitOfMeasurement}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Količina po Transportnom Pakovanju
                            </label>
                            <input
                                type="number"
                                name="quantityPerTransportPackage"
                                value={formik.values.quantityPerTransportPackage}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Minimalna Količina za Porudžbinu
                            </label>
                            <input
                                type="number"
                                name="minimumQuantityDemand"
                                value={formik.values.minimumQuantityDemand}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Bruto Masa
                            </label>
                            <input
                                type="number"
                                name="brutoMass"
                                value={formik.values.brutoMass}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Početna Količina
                            </label>
                            <input
                                type="number"
                                name="startingQuantityDemand"
                                value={formik.values.startingQuantityDemand}
                                onChange={formik.handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Maksimalna Količina
                            </label>
                            <input
                                type="number"
                                name="maximumQuantityDemand"
                                value={formik.values.maximumQuantityDemand}
                                onChange={formik.handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Minimalna Temperatura
                            </label>
                            <input
                                type="number"
                                name="minimumTemperature"
                                value={formik.values.minimumTemperature}
                                onChange={formik.handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Maksimalna Temperatura
                            </label>
                            <input
                                type="number"
                                name="maximumTemperature"
                                value={formik.values.maximumTemperature}
                                onChange={formik.handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Rok Trajanja (u minutama)
                            </label>
                            <input
                                type="number"
                                name="expiryDateInMinutes"
                                value={formik.values.expiryDateInMinutes}
                                onChange={formik.handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Maloprodajna Cena
                            </label>
                            <input
                                type="number"
                                name="retailPrice"
                                value={formik.values.retailPrice}
                                onChange={formik.handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                            />
                        </div>
                    </FormSection>

                    <FormSection title="Status">
                        <div className="col-span-2 space-y-4">
                            <div className="flex items-center space-x-6">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name="isActive"
                                        value="true"
                                        checked={formik.values.isActive === 'true'}
                                        onChange={formik.handleChange}
                                        className="h-4 w-4 text-blue-600"
                                    />
                                    <span>Aktivan</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name="isActive"
                                        value="false"
                                        checked={formik.values.isActive === 'false'}
                                        onChange={formik.handleChange}
                                        className="h-4 w-4 text-blue-600"
                                    />
                                    <span>Neaktivan</span>
                                </label>
                            </div>
                            <div className="flex items-center space-x-6">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name="isVisible"
                                        value="true"
                                        checked={formik.values.isVisible === 'true'}
                                        onChange={formik.handleChange}
                                        className="h-4 w-4 text-blue-600"
                                    />
                                    <span>Vidljiv</span>
                                </label>
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="radio"
                                        name="isVisible"
                                        value="false"
                                        checked={formik.values.isVisible === 'false'}
                                        onChange={formik.handleChange}
                                        className="h-4 w-4 text-blue-600"
                                    />
                                    <span>Nevidljiv</span>
                                </label>
                            </div>
                        </div>
                    </FormSection>

                    <FormSection title="Slika Proizvoda">
                        <div className="col-span-2">
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                                <div className="space-y-1 text-center">
                                    <svg
                                        className="mx-auto h-12 w-12 text-gray-400"
                                        stroke="currentColor"
                                        fill="none"
                                        viewBox="0 0 48 48"
                                    >
                                        <path
                                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <div className="flex text-sm text-gray-600">
                                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                            <span>Otpremi sliku</span>
                                            <input
                                                type="file"
                                                className="sr-only"
                                                onChange={handleImageChange}
                                                accept="image/*"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {existingImage && (
                                <div className="mt-4">
                                    <p className="text-sm font-medium text-gray-700">Postojeća slika:</p>
                                    <img
                                        src={existingImage}
                                        alt="Postojeća slika"
                                        className="mt-2 h-32 w-32 object-contain border rounded-lg"
                                    />
                                </div>
                            )}
                        </div>
                    </FormSection>

                    <div className="flex justify-end space-x-4 pt-6">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => navigate('/articles')}
                        >
                            Odustani
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                        >
                            Sačuvaj Izmene
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditArticle;
